<script lang="ts">
	import Button from '$lib/buttons/v3/button.svelte';
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import { editable } from '$lib/storyblok/editable';
	import type { ISbStoryData } from '@storyblok/js';
	import type { ImageBlock } from '../imageBlock.svelte';
	import Blok from '$lib/storyblok/blok.svelte';
	import type { WasteEducationTabContent as TabContent } from './wasteEducationTabContent.svelte';
	import WasteEducationTabContent from './wasteEducationTabContent.svelte';

	interface Props {
		content: ISbStoryData<{
			headline: string;
			subheadline: string;
			tab_items: Array<{
				title: string;
				tab_content: Array<TabContent>;
				image: Array<ISbStoryData<ImageBlock>>;
			}>;
		}>['content'];
	}

	let { content }: Props = $props();

	let activeTab = $state(0);
	let activeTabContent = $state<Record<number, number | null>>({});
</script>

<section use:editable={content} class="bg-white py-13 text-brown-1">
	<PageGrid16 margin>
		<div class="col-span-full lg:col-start-3 lg:col-end-[15]">
			<h1
				class="mx-auto mb-4 max-w-[623px] text-pretty text-center text-[36px]/[112%] tracking-[-.36px] lg:mb-[14px] lg:text-[48px]/[120%] lg:tracking-[-.48px]"
			>
				{content.headline}
			</h1>
			<div
				class="mx-auto max-w-[623px] text-pretty text-center text-[14px]/[160%] lg:text-[16px]/[150%]"
			>
				{content.subheadline}
			</div>
			<div class="relative -mx-5 py-7 lg:pb-8 lg:pt-12">
				<div class="scrollbar-hide flex gap-x-3 overflow-y-auto px-5 lg:justify-center lg:gap-x-4">
					{#each content.tab_items as tab, i}
						{@const isActive = i === activeTab}
						<Button
							class={['btn-small btn-brown', { 'btn-secondary': !isActive }]}
							onclick={() => (activeTab = i)}
						>
							{tab.title}
						</Button>
					{/each}
				</div>
				<div
					class="pointer-events-none absolute inset-0"
					style="background: linear-gradient(90deg, #FFFFFF 0%, transparent 7%, transparent 93%, #FFFFFF 100%);"
				></div>
			</div>
			<div class="grid grid-cols-1">
				{#each content.tab_items as tabItem, i}
					<article
						class={[
							'col-span-full row-start-1 flex overflow-hidden rounded-2xl bg-brown-6 transition-opacity duration-600 max-lg:flex-col max-lg:justify-between',
							{ hidden: activeTab !== i }
						]}
					>
						<div
							class="flex animate-fadeIn flex-col gap-y-5 px-5 pt-5 max-lg:mb-3 lg:w-1/2 lg:px-8 lg:pb-15 lg:pt-10"
						>
							{#each tabItem.tab_content as tabContent, i}
								<WasteEducationTabContent
									content={tabContent}
									expandable={tabItem.tab_content.length > 1}
									expanded={activeTabContent[i] === i ||
										(i === 0 && activeTabContent[i] === undefined)}
									onexpand={() => (activeTabContent[i] = i)}
									onclose={() => (activeTabContent[i] = null)}
								/>
							{/each}
						</div>
						{#each tabItem.image as image}
							<div class="lg:aspect-square lg:w-1/2">
								<Blok content={{ ...image, rounded_corners: 'none' }} />
							</div>
						{/each}
					</article>
				{/each}
			</div>
		</div>
	</PageGrid16>
</section>
